// Import the EventSource polyfill that supports custom headers
import { EventSourcePolyfill } from 'event-source-polyfill';
import type { ChatEvent, ChatEventHandler } from '../types';

export interface EventSourceConfig {
  url: string;
  jwt?: string;
  organizationId?: string;
  lastEventId?: string;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatTimeout?: number;
}

/**
 * EventSourceService - Real-time event handling using EventSourcePolyfill
 *
 * This service uses EventSourcePolyfill to support custom headers required by Salesforce,
 * matching the implementation from the working sample application.
 */
export class EventSourceService {
  private eventSource: EventSource | null = null;

  private config: EventSourceConfig;

  private handlers: Set<ChatEventHandler> = new Set();

  private reconnectAttempts = 0;

  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  private isConnected = false;

  private shouldReconnect = true;

  constructor(config: EventSourceConfig) {
    this.config = {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
      heartbeatTimeout: 90000,
      ...config,
    };
  }

  /**
   * Add an event handler
   */
  addHandler(handler: ChatEventHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  /**
   * Connect to the Salesforce event stream
   */
  connect(): Promise<void> {
    // Check if already connected and working
    if (this.isConnected && this.eventSource?.readyState === 1) {
      // eslint-disable-next-line no-console
      console.log('⚠️ EventSource already connected and ready');
      return Promise.resolve();
    }

    // Clean up any existing connection before creating new one
    if (this.eventSource) {
      // eslint-disable-next-line no-console
      console.log('🧹 Cleaning up existing EventSource before reconnecting');
      // ✅ FIX: Await the disconnect to ensure cleanup completes
      return this.disconnect().then(() => {
        this.shouldReconnect = true;
        return new Promise((resolve, reject) => {
          try {
            this.createEventSource(resolve, reject);
          } catch (error) {
            reject(error);
          }
        });
      });
    }

    return new Promise((resolve, reject) => {
      try {
        this.shouldReconnect = true;
        this.createEventSource(resolve, reject);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the event stream
   */
  disconnect(): Promise<void> {
    return new Promise((resolve) => {
      this.shouldReconnect = false;

      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = undefined;
      }

      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }

      this.isConnected = false;
      resolve();
    });
  }

  /**
   * Update configuration (useful for token refresh)
   */
  updateConfig(updates: Partial<EventSourceConfig>): void {
    this.config = { ...this.config, ...updates };
    // eslint-disable-next-line no-console
    console.log('🔧 EventSource config updated:', Object.keys(updates));
  }

  private createEventSource(
    resolve: () => void,
    reject: (error: unknown) => void
  ): void {
    try {
      // Use EventSource polyfill with proper headers (like the sample app)
      // eslint-disable-next-line no-console
      console.log('🔧 Using EventSourcePolyfill with proper headers');
      // eslint-disable-next-line no-console
      console.log(
        '🔍 EventSourcePolyfill available:',
        typeof EventSourcePolyfill
      );
      // eslint-disable-next-line no-console
      console.log('🔍 EventSourcePolyfill constructor:', EventSourcePolyfill);

      // Build the SSE URL with timestamp (no auth in URL)
      const sseUrl = `${this.config.url}/eventrouter/v1/sse?_ts=${Date.now()}`;

      // Prepare headers for Salesforce (matching sample app exactly)
      const headers: Record<string, string> = {};

      if (this.config.jwt) {
        headers.Authorization = `Bearer ${this.config.jwt}`;
      }

      if (this.config.organizationId) {
        headers['X-Org-ID'] = this.config.organizationId;
      }

      // Only include Last-Event-ID header if we have a value (like sample app)
      if (this.config.lastEventId) {
        headers['Last-Event-ID'] = this.config.lastEventId;
      }

      // eslint-disable-next-line no-console
      console.log('🔌 Connecting to Salesforce EventSource:', sseUrl);
      // eslint-disable-next-line no-console
      console.log('🔑 Using headers:', Object.keys(headers));
      // eslint-disable-next-line no-console
      console.log('🔧 Full headers:', headers);

      // eslint-disable-next-line no-console
      console.log('🔧 About to create EventSourcePolyfill...');

      try {
        // eslint-disable-next-line no-console
        console.log('🔧 Creating EventSourcePolyfill with options:', {
          url: sseUrl,
          hasHeaders: !!headers,
          headerCount: Object.keys(headers).length,
          heartbeatTimeout: this.config.heartbeatTimeout,
        });

        // Create EventSourcePolyfill matching sample app format
        this.eventSource = new EventSourcePolyfill(sseUrl, {
          headers,
          heartbeatTimeout: this.config.heartbeatTimeout!,
        });

        // eslint-disable-next-line no-console
        console.log(
          '✅ EventSourcePolyfill created successfully, readyState:',
          this.eventSource?.readyState
        );

        // Set up event handlers immediately after creation
        this.setupPolyfillEventHandlers(resolve, reject);

        // If we get here, the polyfill worked - don't fall back to native
        return;
      } catch (polyfillError) {
        // eslint-disable-next-line no-console
        console.error('❌ EventSourcePolyfill creation failed:', polyfillError);
        // eslint-disable-next-line no-console
        console.error('❌ Error details:', {
          name: (polyfillError as Error)?.name,
          message: (polyfillError as Error)?.message,
          stack: (polyfillError as Error)?.stack?.split('\n').slice(0, 3),
        });
        // eslint-disable-next-line no-console
        console.log('🔄 Falling back to native EventSource without headers');

        // Fallback to native EventSource (will fail auth but at least we can see the connection attempt)
        this.eventSource = new EventSource(sseUrl);

        // eslint-disable-next-line no-console
        console.log(
          '🔗 Native EventSource created, readyState:',
          this.eventSource?.readyState
        );
      }

      // Add timeout to detect hanging connections
      const connectionTimeout = setTimeout(() => {
        if (this.eventSource?.readyState === 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '⚠️ EventSource connection timeout - still in CONNECTING state after 15 seconds'
          );
          // eslint-disable-next-line no-console
          console.log('🔍 EventSource URL:', sseUrl);
          // eslint-disable-next-line no-console
          console.log('🔍 EventSource headers:', headers);
        }
      }, 15000);

      this.eventSource!.onopen = () => {
        clearTimeout(connectionTimeout);
        // eslint-disable-next-line no-console
        console.log('✅ EventSource connection opened');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.setupEventListeners();
        resolve();
      };

      this.eventSource!.onerror = (error) => {
        clearTimeout(connectionTimeout);
        // eslint-disable-next-line no-console
        console.error('❌ EventSource connection error:', error);
        // eslint-disable-next-line no-console
        console.error(
          '❌ EventSource readyState:',
          this.eventSource?.readyState
        );
        this.isConnected = false;

        if (
          this.shouldReconnect &&
          this.reconnectAttempts < this.config.maxReconnectAttempts!
        ) {
          this.scheduleReconnect();
        } else {
          this.notifyHandlers({
            type: 'error',
            payload: { error: 'EventSource connection failed', details: error },
          });
          reject(error);
        }
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Failed to create EventSource:', error);
      reject(error);
    }
  }

  private setupPolyfillEventHandlers(
    resolve: () => void,
    reject: (error: unknown) => void
  ): void {
    if (!this.eventSource) {
      reject(new Error('EventSource not created'));
      return;
    }

    // Add timeout to detect hanging connections
    const connectionTimeout = setTimeout(() => {
      if (this.eventSource?.readyState === 0) {
        // eslint-disable-next-line no-console
        console.warn(
          '⚠️ EventSource connection timeout - still in CONNECTING state after 15 seconds'
        );
      }
    }, 15000);

    this.eventSource.onopen = () => {
      clearTimeout(connectionTimeout);
      // eslint-disable-next-line no-console
      console.log('✅ EventSource connection opened');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.setupEventListeners();
      resolve();
    };

    this.eventSource.onerror = (error) => {
      clearTimeout(connectionTimeout);
      // eslint-disable-next-line no-console
      console.error('❌ EventSource connection error:', error);
      // eslint-disable-next-line no-console
      console.error('❌ EventSource readyState:', this.eventSource?.readyState);

      //Only treat as fatal error if connection never opened or is closed
      // readyState 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
      const isConnectionClosed = this.eventSource?.readyState === 2;

      if (isConnectionClosed) {
        this.isConnected = false;

        if (
          this.shouldReconnect &&
          this.reconnectAttempts < this.config.maxReconnectAttempts!
        ) {
          this.scheduleReconnect();
        } else {
          this.notifyHandlers({
            type: 'error',
            payload: { error: 'EventSource connection failed', details: error },
          });
          reject(error);
        }
      } else {
        // Transient error on open connection - just log it
        console.warn('⚠️ Transient EventSource error (connection still open)');
      }
    };
  }

  private setupEventListeners(): void {
    if (!this.eventSource) {
      return;
    }

    // Listen for Salesforce-specific events based on the sample app
    const eventTypes = [
      'CONVERSATION_MESSAGE',
      'CONVERSATION_ROUTING_RESULT',
      'CONVERSATION_PARTICIPANT_CHANGED',
      'CONVERSATION_TYPING_STARTED_INDICATOR',
      'CONVERSATION_TYPING_STOPPED_INDICATOR',
      'CONVERSATION_DELIVERY_ACKNOWLEDGEMENT',
      'CONVERSATION_READ_ACKNOWLEDGEMENT',
      'CONVERSATION_CLOSE_CONVERSATION',
    ];

    eventTypes.forEach((eventType) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.eventSource!.addEventListener(eventType, (event: any) => {
        try {
          const data = JSON.parse(event.data);
          // eslint-disable-next-line no-console
          console.log(`📨 Received ${eventType} event:`, data);

          // Transform Salesforce events to our internal format
          this.handleSalesforceEvent(eventType, data);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(`Failed to parse ${eventType} event:`, error);
        }
      });
    });
  }

  private handleSalesforceEvent(
    eventType: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): void {
    switch (eventType) {
      case 'CONVERSATION_MESSAGE':
        this.notifyHandlers({
          type: 'messageReceived',
          payload: data,
        });
        break;
      case 'CONVERSATION_CLOSE_CONVERSATION':
        this.notifyHandlers({
          type: 'conversationEnded',
          payload: data,
        });
        break;
      case 'CONVERSATION_ROUTING_RESULT':
        this.notifyHandlers({
          type: 'routingResult',
          payload: data,
        });
        break;
      case 'CONVERSATION_PARTICIPANT_CHANGED':
        // Handle agent joining/leaving the conversation
        this.notifyHandlers({
          type: 'participantChanged',
          payload: data,
        });
        break;
      case 'CONVERSATION_TYPING_STARTED_INDICATOR':
      case 'CONVERSATION_TYPING_STOPPED_INDICATOR':
      case 'CONVERSATION_DELIVERY_ACKNOWLEDGEMENT':
      case 'CONVERSATION_READ_ACKNOWLEDGEMENT':
        // These are informational events we don't need to process
        // eslint-disable-next-line no-console
        console.log(`ℹ️ Ignoring informational event: ${eventType}`);
        break;

      default:
        // eslint-disable-next-line no-console
        console.log(`Unhandled Salesforce event type: ${eventType}`, data);
        // Send as generic error event for unhandled types
        this.notifyHandlers({
          type: 'error',
          payload: { error: `Unhandled event type: ${eventType}`, data },
        });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay =
      this.config.reconnectInterval! * 2 ** (this.reconnectAttempts - 1);

    // eslint-disable-next-line no-console
    console.log(
      `🔄 Scheduling reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts} in ${delay}ms`
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.shouldReconnect) {
        this.disconnect().then(() => {
          this.createEventSource(
            () => {
              // eslint-disable-next-line no-console
              console.log('✅ Reconnection successful');
            },
            (error) => {
              console.error('❌ Reconnection failed:', error);
            }
          );
        });
      }
    }, delay);
  }

  private notifyHandlers(event: ChatEvent<unknown>): void {
    this.handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in event handler:', error);
      }
    });
  }

  /**
   * Get connection status
   */
  isConnectionOpen(): boolean {
    return this.isConnected;
  }
}
